import { Link } from 'react-router-dom';
import {
  useListEvents,
  useGetEventAttendance,
  useListStudents,
  useListVolunteers,
  useListAuditSignouts,
  useListPhoneApprovals,
  useListRollCalls,
  useGetRollCallEntries,
  getGetRollCallEntriesQueryKey,
  type AttendanceRecord,
  type CheckoutType,
  type Event as EventRecord
} from '@cdf/api-client-react';
import {
  ArrowRightIcon,
  ArrowUpRightIcon,
  CalendarDaysIcon,
  CheckCircle2Icon,
  ClipboardListIcon,
  HeartHandshakeIcon,
  MapPinIcon,
  MessageSquareTextIcon,
  UsersIcon,
  AlertTriangleIcon
} from 'lucide-react';

import { useSession } from '@/components/auth/user-provider';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/layout/page-header';
import { LayoutDashboardIcon } from '@/lib/navigation';

/** Today's local date as `YYYY-MM-DD` (avoids UTC off-by-one). */
function todayLocalISO(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Format a calendar date for AU display (e.g. "Thu 13 Aug"). */
function formatEventDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  return new Date(y, m - 1, d).toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
}

/** Format a "HH:MM" time-of-day string for display. */
function formatTimeOfDay(time: string): string {
  const parts = time.split(':');
  const h = Number(parts[0] ?? 0);
  const min = Number(parts[1] ?? 0);
  const dt = new Date();
  dt.setHours(h, min, 0, 0);
  return dt.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

function formatTimeRange(start: string | null, end: string | null): string | null {
  if (!start && !end) return null;
  if (start && end) return `${formatTimeOfDay(start)} – ${formatTimeOfDay(end)}`;
  if (start) return `from ${formatTimeOfDay(start)}`;
  return `until ${formatTimeOfDay(end!)}`;
}

function compareEventsBySchedule(a: EventRecord, b: EventRecord): number {
  if (a.date !== b.date) return a.date.localeCompare(b.date);
  return (a.startTime ?? '').localeCompare(b.startTime ?? '');
}

/**
 * Next up to 3 events for the dashboard register shortcuts:
 * prefer today/future active sessions chronologically; if none remain,
 * fall back to the most recent active events.
 */
function pickUpcomingEvents(events: EventRecord[]): EventRecord[] {
  const active = events.filter((e) => e.status === 'active');
  const today = todayLocalISO();
  const upcoming = active.filter((e) => e.date >= today).sort(compareEventsBySchedule);
  if (upcoming.length > 0) return upcoming.slice(0, 3);
  return [...active].sort((a, b) => compareEventsBySchedule(b, a)).slice(0, 3);
}

function eventWhenLabel(event: EventRecord): string {
  const dateLabel = formatEventDate(event.date);
  const timeLabel = formatTimeRange(event.startTime, event.endTime);
  return timeLabel ? `${dateLabel} · ${timeLabel}` : dateLabel;
}

type StatCardTone = {
  card: string;
  icon: string;
  accent: string;
};

type StatCard = {
  title: string;
  value: number | undefined;
  icon: typeof UsersIcon;
  hint: string;
  link?: string;
  tone: StatCardTone;
};

function preferenceBadge(pref?: CheckoutType | null): { label: string; className: string } {
  switch (pref) {
    case 'by_self':
      return {
        label: 'By Self',
        className: 'border-transparent bg-success text-success-foreground'
      };
    case 'wait_for_parent':
      return {
        label: 'Wait for Parent',
        className: 'border-transparent bg-warning text-warning-foreground'
      };
    case 'back_to_school':
      return {
        label: 'Return to School',
        className: 'border-transparent bg-info text-info-foreground'
      };
    case 'with_person':
      return {
        label: 'With Named Person',
        className: 'border-transparent bg-primary text-primary-foreground'
      };
    case 'with_teacher':
      return {
        label: 'With Teacher',
        className: 'border-transparent bg-secondary text-secondary-foreground'
      };
    default:
      return {
        label: 'No Preference',
        className: 'border-transparent bg-muted text-muted-foreground'
      };
  }
}

function StatCards() {
  const { data: eventData, isPending: eventsPending } = useListEvents();
  const { data: studentData, isPending: studentsPending } = useListStudents();
  const { data: volunteerData, isPending: volunteersPending } = useListVolunteers();
  const { data: auditData, isPending: auditPending } = useListAuditSignouts();

  const activeEvents = eventData?.events?.filter((e) => e.status === 'active') ?? [];
  const totalStudents = studentData?.total ?? studentData?.students?.length;
  const inductedVolunteers =
    volunteerData?.volunteers?.filter((v) => v.isInducted).length ?? 0;
  const totalVolunteers = volunteerData?.total ?? volunteerData?.volunteers?.length;
  const smsCount = auditData?.entries?.filter((e) => e.type === 'sms').length ?? 0;

  const cards: StatCard[] = [
    {
      title: 'Active Events',
      value: activeEvents.length,
      icon: CalendarDaysIcon,
      hint: activeEvents[0]?.name ?? 'No active events',
      link: '/events',
      tone: {
        card: 'border-success/30 hover:border-success/60',
        icon: 'bg-success/15 text-success',
        accent: 'bg-success'
      }
    },
    {
      title: 'Students',
      value: totalStudents,
      icon: UsersIcon,
      hint: 'Enrolled in system',
      link: '/students',
      tone: {
        card: 'border-info/30 hover:border-info/60',
        icon: 'bg-info/15 text-info',
        accent: 'bg-info'
      }
    },
    {
      title: 'Volunteers',
      value: totalVolunteers,
      icon: HeartHandshakeIcon,
      hint: `${inductedVolunteers} inducted`,
      link: '/volunteers',
      tone: {
        card: 'border-primary/30 hover:border-primary/60',
        icon: 'bg-primary/12 text-primary',
        accent: 'bg-primary'
      }
    },
    {
      title: 'SMS Received',
      value: smsCount,
      icon: MessageSquareTextIcon,
      hint: 'Parent messages logged',
      link: '/audit',
      tone: {
        card: 'border-warning/35 hover:border-warning/70',
        icon: 'bg-warning/20 text-warning-foreground',
        accent: 'bg-warning'
      }
    }
  ];

  const pending = [eventsPending, studentsPending, volunteersPending, auditPending];

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {cards.map((card, i) => (
        <Link
          key={card.title}
          to={card.link ?? '#'}
          className="group block rounded-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/20"
        >
          <Card
            className={`relative min-h-40 gap-0 overflow-hidden rounded-lg border py-0 shadow-sm shadow-primary/10 transition-all duration-200 ease-out group-hover:-translate-y-0.5 group-hover:shadow-md group-focus-visible:border-primary ${card.tone.card}`}
          >
            <div aria-hidden="true" className={`absolute inset-x-0 top-0 h-1 ${card.tone.accent}`} />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 px-5 pt-5 pb-3">
              <CardTitle className="text-sm font-semibold tracking-tight">{card.title}</CardTitle>
              <span className={`rounded-lg p-2.5 ${card.tone.icon}`}>
                <card.icon aria-hidden="true" className="size-4" />
              </span>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col justify-end px-5 pb-5">
              {pending[i] ? (
                <Skeleton className="h-9 w-16" />
              ) : (
                <div className="text-3xl font-bold tracking-tight tabular-nums">{card.value ?? '—'}</div>
              )}
              <div className="mt-1 flex items-center justify-between gap-3">
                <p className="text-muted-foreground truncate text-xs">{card.hint}</p>
                <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary transition-colors group-hover:text-primary/80">
                  View
                  <ArrowUpRightIcon aria-hidden="true" className="size-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

/** Compact per-roll-call progress chip for the dashboard breakdown. */
function RollCallMiniProgress({ rollCallId, name, isLatest }: { rollCallId: string; name: string; isLatest: boolean }) {
  const { data } = useGetRollCallEntries(rollCallId);
  const entries = data?.entries ?? [];
  const present = entries.filter((e) => e.status === 'present').length;
  const total = entries.length;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
        isLatest
          ? 'border-primary/30 bg-primary/5 text-primary'
          : 'border-border bg-muted/50 text-muted-foreground'
      }`}
    >
      {isLatest && <span aria-hidden className="size-1.5 rounded-full bg-primary" />}
      {name}
      <span className="tabular-nums">{present}/{total}</span>
    </span>
  );
}

/** Attendance body — only mounts when an active event exists. */
function AttendanceBody({ eventId }: { eventId: string }) {
  const { data: attendanceData, isPending: attendancePending } =
    useGetEventAttendance(eventId);
  const { data: rollCallsData } = useListRollCalls(eventId);
  const rollCalls = rollCallsData?.rollCalls ?? [];
  const latestRollCall = rollCalls.length > 0 ? rollCalls[rollCalls.length - 1] : null;
  const { data: latestEntriesData } = useGetRollCallEntries(latestRollCall?.id ?? '', {
    query: { enabled: !!latestRollCall?.id, queryKey: getGetRollCallEntriesQueryKey(latestRollCall?.id ?? '') }
  });
  const latestEntries = latestEntriesData?.entries ?? [];

  const records: AttendanceRecord[] = attendanceData?.attendance ?? [];

  // Group by SMS preference
  const byPreference = {
    by_self: records.filter((r) => r.smsPreference === 'by_self'),
    wait_for_parent: records.filter((r) => r.smsPreference === 'wait_for_parent'),
    with_person: records.filter((r) => r.smsPreference === 'with_person'),
    with_teacher: records.filter((r) => r.smsPreference === 'with_teacher'),
    back_to_school: records.filter((r) => r.smsPreference === 'back_to_school'),
    none: records.filter((r) => !r.smsPreference)
  };

  const signedOut = records.filter((r) => r.checkinStatus === 'signed_out').length;
  const rollPresent = latestEntries.filter((e) => e.status === 'present').length;
  const rollAbsent = latestEntries.filter((e) => e.status === 'absent').length;
  const rollPending = latestEntries.filter((e) => e.status === 'pending').length;
  const metrics = [
    { label: 'Present', value: rollPresent, accent: 'bg-success' },
    { label: 'Absent', value: rollAbsent, accent: 'bg-destructive' },
    { label: 'Pending', value: rollPending, accent: 'bg-muted-foreground' },
    { label: 'Signed Out', value: signedOut, accent: 'bg-info' },
    { label: 'Total', value: records.length, accent: 'bg-primary' }
  ];

  if (attendancePending) {
    return <Skeleton className="h-32 w-full" />;
  }

  if (records.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No students checked in for this event yet.
      </p>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-2 rounded-lg border border-primary/10 bg-muted/60 p-2.5 shadow-xs sm:grid-cols-5 sm:gap-3 sm:p-3">
        {metrics.map((metric) => (
          <div key={metric.label} className="rounded-md border border-border/70 bg-background/70 p-3 shadow-xs">
            <span aria-hidden="true" className={`mb-2 block size-1.5 rounded-full ${metric.accent}`} />
            <div className="text-xl font-bold tracking-tight tabular-nums">{metric.value}</div>
            <p className="text-muted-foreground text-xs">{metric.label}</p>
          </div>
        ))}
      </div>

      {latestRollCall && (
        <p className="text-xs font-medium text-muted-foreground">
          Headline: <span className="text-foreground">{latestRollCall.name}</span>
        </p>
      )}

      {rollCalls.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          {rollCalls.map((rc, idx) => {
            const isLatest = idx === rollCalls.length - 1;
            return (
              <RollCallMiniProgress
                key={rc.id}
                rollCallId={rc.id}
                name={rc.name}
                isLatest={isLatest}
              />
            );
          })}
        </div>
      )}

      <div className="space-y-2">
        {(
          [
            ['by_self', 'By Self'],
            ['wait_for_parent', 'Wait for Parent'],
            ['with_person', 'With Named Person'],
            ['with_teacher', 'With Teacher'],
            ['back_to_school', 'Return to School'],
            ['none', 'No Preference']
          ] as const
        ).map(([key, label]) => {
          const list = byPreference[key];
          if (list.length === 0) return null;
          const badge = preferenceBadge(key === 'none' ? null : key);
          return (
            <div
              key={key}
              className="flex items-center justify-between rounded-md border border-border/60 bg-background/70 px-3 py-2 shadow-xs transition-colors hover:bg-accent/60"
            >
              <Badge variant="outline" className={badge.className}>
                {label}
              </Badge>
              <span className="text-muted-foreground text-sm font-medium tabular-nums">
                {list.length} student{list.length !== 1 ? 's' : ''}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}

/** Breakdown of students by SMS preference for the active event. */
function AttendanceBreakdown() {
  const { data: eventData, isPending: eventsPending } = useListEvents();
  const activeEvent = eventData?.events?.find((e) => e.status === 'active');

  return (
    <Card className="gap-0 overflow-hidden rounded-lg border-primary/20 py-0 shadow-sm shadow-primary/10">
      <CardHeader className="gap-2 border-b border-primary/10 bg-primary/5 px-5 py-4">
        <CardTitle className="flex items-center gap-2.5 text-sm font-semibold tracking-tight">
          <span className="rounded-lg bg-primary/10 p-2 text-primary">
            <ClipboardListIcon aria-hidden="true" className="size-4" />
          </span>
          Live Event Attendance
        </CardTitle>
        <CardDescription>
          {activeEvent
            ? `${activeEvent.name} — ${activeEvent.location}`
            : 'No active event right now'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 px-5 py-5">
        {eventsPending ? (
          <Skeleton className="h-32 w-full" />
        ) : activeEvent ? (
          <>
            <AttendanceBody eventId={activeEvent.id} />
            <Badge asChild variant="outline" className="border-primary/20 bg-primary/5 text-primary hover:bg-primary/10">
              <Link to={`/events/${activeEvent.id}`}>
                Open workspace
                <ArrowUpRightIcon aria-hidden="true" />
              </Link>
            </Badge>
          </>
        ) : (
          <p className="text-muted-foreground text-sm">No active event right now.</p>
        )}
      </CardContent>
    </Card>
  );
}

/** Recent audit entries — sign-outs and SMS trail. */
function RecentActivity() {
  const { data, isPending } = useListAuditSignouts();
  const entries = data?.entries ?? [];

  return (
    <Card className="gap-0 overflow-hidden rounded-lg border-info/20 py-0 shadow-sm shadow-info/10">
      <CardHeader className="gap-2 border-b border-info/10 bg-info/5 px-5 py-4">
        <CardTitle className="flex items-center gap-2.5 text-sm font-semibold tracking-tight">
          <span className="rounded-lg bg-info/10 p-2 text-info">
            <CheckCircle2Icon aria-hidden="true" className="size-4" />
          </span>
          Recent Activity
        </CardTitle>
        <CardDescription>Latest sign-outs and parent SMS messages</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 px-5 py-5">
        {isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : entries.length === 0 ? (
          <p className="text-muted-foreground text-sm">No activity recorded yet.</p>
        ) : (
          entries.slice(0, 6).map((entry) => (
            <div
              key={`${entry.type}-${entry.timestamp}-${entry.studentName ?? 'unknown'}`}
              className="group flex items-start gap-3 rounded-lg border border-transparent bg-muted/35 p-3 shadow-xs transition-all hover:border-primary/15 hover:bg-muted/70 hover:shadow-sm"
            >
              <Badge
                className={`mt-0.5 ${entry.type === 'sms' ? 'border-transparent bg-info text-info-foreground' : 'border-transparent bg-success text-success-foreground'}`}
              >
                {entry.type === 'sms' ? 'SMS' : 'Sign-Out'}
              </Badge>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">
                  {entry.studentName ?? 'Unknown student'}
                </p>
                <p className="text-muted-foreground truncate text-xs leading-5">
                  {entry.type === 'sms'
                    ? entry.messageBody
                    : `${entry.action ?? 'Signed out'}${entry.checkoutType ? ` · ${entry.checkoutType}` : ''}${entry.authorizedPersonName ? ` · with ${entry.authorizedPersonName}` : ''}`}
                </p>
              </div>
              <span className="text-muted-foreground shrink-0 rounded-sm bg-background/70 px-1.5 py-1 text-xs font-medium tabular-nums">
                {new Date(entry.timestamp).toLocaleTimeString('en-AU', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function PendingApprovalsAlert() {
  const { session } = useSession();
  const isAdmin = session.status === 'signedIn' && session.user.role === 'admin';
  const { data, isPending } = useListPhoneApprovals({ status: 'pending' });
  const count = data?.total ?? 0;

  if (!isAdmin || isPending || count === 0) return null;

  return (
    <Link to="/approved-numbers" className="block rounded-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/20">
      <Card className="flex items-center gap-3 border-destructive/30 bg-destructive/5 p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
        <span className="rounded-xl bg-destructive/15 p-2.5 text-destructive">
          <AlertTriangleIcon aria-hidden className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold tracking-tight">
            {count} phone number{count !== 1 ? 's' : ''} awaiting approval
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Parent texts from unapproved numbers need review before their pickup preferences can be applied.
          </p>
        </div>
        <ArrowUpRightIcon aria-hidden className="size-4 shrink-0 text-destructive" />
      </Card>
    </Link>
  );
}

/** Large next-event register shortcut + two smaller upcoming event tiles. */
function UpcomingRegisterTiles() {
  const { data: eventData, isPending } = useListEvents();
  const upcoming = pickUpcomingEvents(eventData?.events ?? []);
  const [nextEvent, ...following] = upcoming;

  if (isPending) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-36 w-full rounded-xl" />
        <div className="grid gap-3 sm:grid-cols-2">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (!nextEvent) {
    return (
      <Card className="border-dashed p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <span className="rounded-xl bg-muted p-2.5 text-muted-foreground">
            <CalendarDaysIcon aria-hidden className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold tracking-tight">No upcoming events</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Create an active event to open its mobile register from the dashboard.
            </p>
          </div>
          <Badge asChild variant="outline" className="shrink-0">
            <Link to="/events">
              Events
              <ArrowUpRightIcon aria-hidden className="size-3.5" />
            </Link>
          </Badge>
        </div>
      </Card>
    );
  }

  const nextWhen = eventWhenLabel(nextEvent);
  const isToday = nextEvent.date === todayLocalISO();

  return (
    <div className="flex flex-col gap-3">
      <Link
        to={`/events/${nextEvent.id}/register`}
        className="group block rounded-xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/20"
      >
        <Card className="relative gap-0 overflow-hidden rounded-xl border-primary/25 bg-gradient-to-br from-primary/10 via-background to-background py-0 shadow-md shadow-primary/10 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:border-primary/45 group-hover:shadow-lg">
          <div aria-hidden className="absolute inset-x-0 top-0 h-1.5 bg-primary" />
          <CardContent className="flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-6 sm:py-6">
            <div className="min-w-0 flex-1 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="border-transparent bg-primary text-primary-foreground">
                  Next register
                </Badge>
                {isToday ? (
                  <Badge variant="outline" className="border-success/30 bg-success/10 text-success">
                    Today
                  </Badge>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">{nextEvent.name}</h2>
                <p className="text-sm text-muted-foreground sm:text-base">
                  Open the mobile roll call and sign-out register for this session.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                  <CalendarDaysIcon aria-hidden className="size-4 shrink-0 text-primary" />
                  {nextWhen}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <MapPinIcon aria-hidden className="size-4 shrink-0" />
                  <span className="truncate">{nextEvent.location}</span>
                </span>
              </div>
            </div>
            <span className="inline-flex min-h-12 shrink-0 items-center justify-center gap-2 self-start rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-sm transition-colors group-hover:bg-primary/90 sm:self-center">
              Open register
              <ArrowRightIcon aria-hidden className="size-4 transition-transform group-hover:translate-x-0.5" />
            </span>
          </CardContent>
        </Card>
      </Link>

      {following.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {following.map((event, index) => {
            const when = eventWhenLabel(event);
            return (
              <Link
                key={event.id}
                to={`/events/${event.id}/register`}
                className="group block rounded-xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/20"
              >
                <Card className="h-full gap-0 rounded-xl border-border/80 py-0 shadow-sm transition-all duration-200 group-hover:-translate-y-0.5 group-hover:border-primary/30 group-hover:shadow-md">
                  <CardContent className="flex h-full flex-col gap-2.5 px-4 py-4">
                    <div className="flex items-start justify-between gap-2">
                      <Badge variant="outline" className="border-muted-foreground/20 text-muted-foreground">
                        Coming up {index + 2}
                      </Badge>
                      <ArrowUpRightIcon
                        aria-hidden
                        className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary"
                      />
                    </div>
                    <p className="truncate text-base font-semibold tracking-tight">{event.name}</p>
                    <p className="text-xs text-muted-foreground">{when}</p>
                    <p className="truncate text-xs text-muted-foreground">{event.location}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function DashboardPage() {
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Dashboard"
        description="Real-time overview of school events, attendance, and parent communications"
        icon={LayoutDashboardIcon}
      />
      <UpcomingRegisterTiles />
      <PendingApprovalsAlert />
      <StatCards />
      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <AttendanceBreakdown />
        <RecentActivity />
      </div>
    </div>
  );
}
